import { fsModalDialogDismissibleClass, fsModalDialogOpenClass, fsModalDialogScrollableClass, fsModalDialogWindowBodyElementScrolledToBottomIntersectionTrackerClass, fsModalDialogWindowBodyElementScrolledToTopIntersectionTrackerClass, fsModalDialogWindowFooterScrollableIndicatorVisibleClass, fsModalDialogWindowHeaderScrollableIndicatorVisibleClass } from '../IFsModalDialog.js';
import { ModalDialogEvent, modalDialogEventType } from './modal-dialog-event.js';
/**
 * Manages a ModalDialog.
 *
 * @fires modal-dialog-close a cancellable event indicating that the Modal Dialog will be closed
 * @fires modal-dialog-open a cancellable event indicating that the Modal Dialog will be opened
 */
export class ModalDialogManager extends EventTarget {
    constructor({ closeButtonElement, closeButtonTooltipElement, focusScopeElement, overlayElement, rootElement, windowHeaderElement, windowBodyElement, windowFooterElement }, bodyElement = document.body, triggerElement) {
        super();
        /**
         * Whether or not the ModalDialogManger is currently "connected", i.e. required event listeners are added and
         * listening.
         */
        this.isConnected = false;
        /**
         * Connects the ModalDialogManager to the DOM elements it is managing, adding required event listeners.
         */
        this.connect = () => {
            window.addEventListener('keydown', this._handleKeydownForEscape);
            this._overlayElement.addEventListener('click', this._handleOverlayClick);
            if (this._triggerElement != null) {
                this._triggerElement.addEventListener('click', this._handleTriggerClick);
            }
            if (this._closeButtonElement != null) {
                this._closeButtonElement.addEventListener('click', this._handleCloseButtonClick);
            }
            if (this._isOpen()) {
                this.open(false);
            }
            if (this._windowBodyElementScrolledToTopIntersectionObserver != null &&
                this._windowBodyElementScrolledToTopIntersectionTracker != null &&
                this._isScrollable()) {
                this._windowBodyElementScrolledToTopIntersectionObserver.observe(this._windowBodyElementScrolledToTopIntersectionTracker);
            }
            if (this._windowBodyElementScrolledToBottomIntersectionObserver != null &&
                this._windowBodyElementScrolledToBottomIntersectionTracker != null &&
                this._isScrollable()) {
                this._windowBodyElementScrolledToBottomIntersectionObserver.observe(this._windowBodyElementScrolledToBottomIntersectionTracker);
            }
            this.isConnected = true;
        };
        /**
         * Disconnects the ModalDialogManager to the DOM elements it is managing, removing attached event listeners.
         */
        this.disconnect = () => {
            window.removeEventListener('keydown', this._handleKeydownForEscape);
            this._overlayElement.removeEventListener('click', this._handleOverlayClick);
            if (this._triggerElement != null) {
                this._triggerElement.removeEventListener('click', this._handleTriggerClick);
            }
            if (this._closeButtonElement != null) {
                this._closeButtonElement.removeEventListener('click', this._handleCloseButtonClick);
            }
            if (this._focusScopeElement != null && this._isOpen()) {
                this._focusScopeElement.clearTrap();
            }
            this._enableBodyScroll();
            if (this._windowBodyElementScrolledToTopIntersectionObserver != null &&
                this._windowBodyElementScrolledToTopIntersectionTracker != null) {
                this._windowBodyElementScrolledToTopIntersectionObserver.unobserve(this._windowBodyElementScrolledToTopIntersectionTracker);
            }
            if (this._windowBodyElementScrolledToBottomIntersectionObserver != null &&
                this._windowBodyElementScrolledToBottomIntersectionTracker != null) {
                this._windowBodyElementScrolledToBottomIntersectionObserver.unobserve(this._windowBodyElementScrolledToBottomIntersectionTracker);
            }
            this.isConnected = false;
        };
        /**
         * Close the managed ModalDialog.
         *
         * @fires close a cancellable event indicating that the Modal Dialog should be closed
         */
        this.close = (publishEvent = true) => {
            /* c8 ignore next 2 */
            /* Impractical to test with current framework. */
            if (publishEvent) {
                const closeEvent = new ModalDialogEvent(modalDialogEventType['modal-dialog-close']);
                const allowed = this.dispatchEvent(closeEvent);
                /* c8 ignore next 4 */
                /* Impractical to test with current framework. */
                if (!allowed) {
                    return;
                }
            }
            this._enableBodyScroll();
            this._rootElement.ariaHidden = 'true';
            this._rootElement.classList.remove(fsModalDialogOpenClass);
            if (this._focusScopeElement != null) {
                this._focusScopeElement.clearTrap();
            }
            if (this._closeButtonTooltipElement != null) {
                this._closeButtonTooltipElement.dangerouslyForceReposition();
            }
        };
        /**
         * Open the managed ModalDialog.
         *
         * @fires open a cancellable event indicating that the Modal Dialog should be opened
         **/
        this.open = (publishEvent = true) => {
            /* c8 ignore next 2 */
            /* Impractical to test with current framework. */
            if (publishEvent) {
                const openEvent = new ModalDialogEvent(modalDialogEventType['modal-dialog-open']);
                const allowed = this.dispatchEvent(openEvent);
                /* c8 ignore next 4 */
                /* Impractical to test with current framework. */
                if (!allowed) {
                    return;
                }
            }
            this._disableBodyScroll();
            this._rootElement.ariaHidden = 'false';
            if (this._closeButtonTooltipElement != null) {
                const repositionTooltipAfterTransition = () => {
                    this._closeButtonTooltipElement.dangerouslyForceReposition();
                    this._rootElement.removeEventListener('transitionend', repositionTooltipAfterTransition);
                };
                this._rootElement.addEventListener('transitionend', repositionTooltipAfterTransition);
            }
            this._rootElement.classList.add(fsModalDialogOpenClass);
            if (this._focusScopeElement != null) {
                this._focusScopeElement.trapFocus();
            }
        };
        /**
         * Called when the user has scrolled to the top of the Modal Dialog window's body.
         * @param entries observed intersections
         */
        this._handleWindowBodyScrolledToTop = (entries) => {
            if (entries.find(({ isIntersecting }) => isIntersecting) != null) {
                this._windowHeaderElement.classList.remove(fsModalDialogWindowHeaderScrollableIndicatorVisibleClass);
            }
            else {
                this._windowHeaderElement.classList.add(fsModalDialogWindowHeaderScrollableIndicatorVisibleClass);
            }
        };
        /**
         * Called when the user has scrolled to the bottom of the Modal Dialog window's body.
         * @param entries observed intersections
         */
        this._handleWindowBodyScrolledToBottom = (entries) => {
            if (entries.find(({ isIntersecting }) => isIntersecting) != null) {
                /* c8 ignore next 2 */
                /* Not practically coverable. */
                this._windowFooterElement.classList.remove(fsModalDialogWindowFooterScrollableIndicatorVisibleClass);
            }
            else {
                this._windowFooterElement.classList.add(fsModalDialogWindowFooterScrollableIndicatorVisibleClass);
            }
        };
        /**
         * Update the close button element. If an existing close button element existed, disassociate it.
         *
         * @param newCloseButtonElement the new close button element.
         */
        this.updateCloseButtonElement = (newCloseButtonElement) => {
            if (this._closeButtonElement != null) {
                this._closeButtonElement.removeEventListener('click', this._handleCloseButtonClick);
            }
            this._closeButtonElement = newCloseButtonElement;
            this._closeButtonElement.addEventListener('click', this._handleCloseButtonClick);
        };
        /**
         * Update the element that should trigger the Modal Dialog to open. If an existing trigger element existed,
         * disassociate it.
         *
         * @param newTriggerElement the new element that should trigger the Modal Dialog to open.
         */
        this.updateTriggerElement = (newTriggerElement) => {
            if (this._triggerElement != null) {
                this._triggerElement.removeEventListener('click', this._handleTriggerClick);
            }
            this._triggerElement = newTriggerElement;
            this._triggerElement.addEventListener('click', this._handleTriggerClick);
        };
        /**
         * Disable scrolling of the HTML Body element and memoize previous styles.
         */
        this._disableBodyScroll = () => {
            this._bodyElementStyleMemo = getComputedStyle(this._bodyElement);
            this._bodyElement.style.touchAction = 'none';
            this._bodyElement.style.overscrollBehavior = 'none';
            this._bodyElement.style.overflowX = 'hidden';
            this._bodyElement.style.overflowY = 'hidden';
        };
        /**
         * Enable scrolling of the HTML Body element and re-implement memoized styles.
         */
        this._enableBodyScroll = () => {
            this._bodyElement.style.touchAction = this._bodyElementStyleMemo.getPropertyValue('touchAction') || '';
            this._bodyElement.style.overscrollBehavior =
                this._bodyElementStyleMemo.getPropertyValue('overscrollBehavior') || '';
            this._bodyElement.style.overflowX = this._bodyElementStyleMemo.getPropertyValue('overflowX') || '';
            this._bodyElement.style.overflowY = this._bodyElementStyleMemo.getPropertyValue('overflowY') || '';
        };
        /**
         * Handle the close button of the Modal Dialog being clicked.
         */
        this._handleCloseButtonClick = () => {
            /* c8 ignore next 4 */
            /* This is a very deep failsafe that is not practically testable. */
            if (!this._isDismissible()) {
                return;
            }
            this.close();
        };
        /**
         * Handle a keydown of the Escape key.
         *
         * @param event the keyboard event for the keydown of the Escape key
         */
        this._handleKeydownForEscape = (event) => {
            if (!this._isDismissible() || !this._isOpen()) {
                return;
            }
            if (event.key !== 'Escape') {
                return;
            }
            this.close();
        };
        /**
         * Handle the Modal Dialog overlay being clicked.
         */
        this._handleOverlayClick = () => {
            if (!this._isDismissible()) {
                return;
            }
            this.close();
        };
        /**
         * Handle the Modal Dialog trigger being clicked.
         */
        this._handleTriggerClick = () => {
            this.open();
        };
        /**
         * Returns whether or not the Modal Dialog is dismissible.
         *
         * @returns whether or not the Modal Dialog is dismissible
         */
        this._isDismissible = () => {
            return this._rootElement.classList.contains(fsModalDialogDismissibleClass);
        };
        /**
         * Returns whether or not the Modal Dialog is open.
         *
         * @returns whether or not the Modal Dialog is open
         */
        this._isOpen = () => {
            return this._rootElement.classList.contains(fsModalDialogOpenClass);
        };
        this._isScrollable = () => {
            return this._rootElement.classList.contains(fsModalDialogScrollableClass);
        };
        this._bodyElement = bodyElement;
        this._closeButtonElement = closeButtonElement;
        this._closeButtonTooltipElement = closeButtonTooltipElement;
        this._focusScopeElement = focusScopeElement;
        this._overlayElement = overlayElement;
        this._rootElement = rootElement;
        this._triggerElement = triggerElement;
        this._windowHeaderElement = windowHeaderElement;
        this._windowBodyElement = windowBodyElement;
        this._windowFooterElement = windowFooterElement;
        this._windowBodyElementScrolledToTopIntersectionTracker = this._windowBodyElement?.querySelector(`.${fsModalDialogWindowBodyElementScrolledToTopIntersectionTrackerClass}`);
        this._windowBodyElementScrolledToBottomIntersectionTracker = this._windowBodyElement?.querySelector(`.${fsModalDialogWindowBodyElementScrolledToBottomIntersectionTrackerClass}`);
        if (this._bodyElement == null) {
            throw new Error('Required element, bodyElement, must be provided');
        }
        if (this._rootElement == null) {
            throw new Error('Required element, rootElement, must be provided');
        }
        this._bodyElementStyleMemo = getComputedStyle(this._bodyElement);
        if (this._windowBodyElementScrolledToTopIntersectionTracker != null) {
            this._windowBodyElementScrolledToTopIntersectionObserver = new IntersectionObserver(this._handleWindowBodyScrolledToTop, { root: this._windowBodyElement, threshold: 1 });
        }
        if (this._windowBodyElementScrolledToBottomIntersectionTracker != null) {
            this._windowBodyElementScrolledToBottomIntersectionObserver = new IntersectionObserver(this._handleWindowBodyScrolledToBottom, { root: this._windowBodyElement, threshold: 1 });
        }
        this.connect();
    }
}
//# sourceMappingURL=modal-dialog-manager.js.map